import { Button, Col, Form, Input, Row, Space, TimePicker } from 'antd';
import {
  add,
  format,
  hoursToMilliseconds,
  minutesToMilliseconds,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  CreateTeamRoleAssignmentEvent,
  StoredTeam,
} from '../../../hooks/types';
import useCreateTeam from '../../../hooks/useCreateTeam';
import { useLoggedInUser } from '../../../hooks/useLoggedInUser';
import { useNotify } from '../../../hooks/useNotify';
import { usePermission } from '../../../hooks/usePermission';
import useQueryCostObjectIds from '../../../hooks/useQueryCostObjectIds';
import { teamByIdQueryName } from '../../../hooks/useQueryTeamById';
import useQueryTeamsByName from '../../../hooks/useQueryTeamsByName';
import useUpdateTeam from '../../../hooks/useUpdateTeam';
import { eventData } from '../../../lib/graphql/common/queries';
import { onApolloError } from '../../../lib/graphql/helpers';
import { nonEmptyString } from '../../../lib/utils';
import { shiftTimeFormat } from '../../../lib/utils/date-time';
import {
  validatorGroupComponentRestrictedSpecialCharacters,
  validatorMaxLength,
  validatorMinLength,
  validatorNoWhiteSpaceOnEnds,
} from '../../../lib/utils/form-validator-builder';
import { errorMessage, successMessage } from '../../../lib/utils/messages';
import Select from '../../AccessibleComponents/Select';
import routes, { routesWithPathParameter } from '../../App/routes';
import PageLoadingSpinner from '../../PageLoadingSpinner';
import { Optional } from '../../Shared';
import {
  groupComponentsMinLength,
  teamNameMaxLength,
} from '../team-validation';
import { TeamInForm, TeamType, TeamTypeValue, teamTypes } from '../types';
import { InputDuration } from './InputDuration';
import ReadonlyTeam from './ReadonlyTeam';
import {
  dayShiftArrayFromUnknownSettings,
  dayShiftFromUnknownSettings,
  dayShiftHoursConstraint,
  defaultDayShiftPeriod,
  getShiftPeriodTime,
} from './util';

type TeamEditorFormProps = {
  readonly currentTeam: StoredTeam;
  readonly isNewTeam: boolean;
};

const defaultTeamType = (teamType: string) => {
  if (
    teamType === TeamType.CostedIMT ||
    teamType === TeamType.LocalIMT ||
    teamType === TeamType.EmergencyOperationsCentre ||
    teamType === TeamType.Section44
  ) {
    return teamType;
  }

  return TeamType.LocalIMT;
};

type IonWithDescription = {
  readonly internal_order_number: string;
  readonly description: string;
};

const teamSettings = (startTime: dayjs.Dayjs, duration: number) => ({
  dayShiftPeriod: [
    startTime.valueOf(),
    startTime.valueOf() + hoursToMilliseconds(duration),
  ],
});

/**
 * Given an internal order number and a (potentially) random list of cost objects,
 * finds and returns the description of that internal order number in the list, if it exists.
 *
 * @param costObjects - a list of cost objects
 * @param ion - the internal order number to find
 * @returns the description of the internal order number, or undefined if it does not exist
 */
const descriptionOfCostObjectId = (
  costObjects: readonly IonWithDescription[] | undefined,
  ion: string
): string | undefined =>
  (costObjects ?? []).find(
    ({ internal_order_number }) => internal_order_number === ion
  )?.description;

/**
 * A Current Team represents the team in remote data and it will be used to initialize the form fields, and also once the mutation
 * is completed (update or create), the current team will be fetched from remote again and update the form fields subsequently.
 * A form for editing team related properties.
 */
const TeamForm = ({ isNewTeam, currentTeam }: TeamEditorFormProps) => {
  const { notify } = useNotify();
  const navigate = useNavigate();

  const [duration, setDuration] = useState<number>(dayShiftHoursConstraint.min);

  const [shiftStartTime, setShiftStartTime] = useState<dayjs.Dayjs | undefined>(
    undefined
  );

  const [endTime, setEndTime] = useState<Date | undefined>(undefined);

  const { loggedInUser } = useLoggedInUser();

  const [form] = Form.useForm<TeamInForm>();

  const [newTeamName, setNewTeamName] = useState<string>(currentTeam.name);

  const [newTeamAlreadyExists, setNewTeamAlreadyExists] = useState(false);

  const [selectedTeamType, setSelectedTeamType] = useState<TeamTypeValue>(
    defaultTeamType(currentTeam.team_type)
  );

  const permission = usePermission(currentTeam.id, loggedInUser);

  const { data: teamNameQueryResult } = useQueryTeamsByName(
    `%${newTeamName}%`,
    onApolloError(notify.error)
  );

  const [updateTeam, { loading: updatingTeam }] = useUpdateTeam(
    onApolloError(notify.error)
  );

  const [createTeam, { loading: creatingTeam }] = useCreateTeam(
    onApolloError(notify.error)
  );

  const { data: activeAndDeletedCostObjects } = useQueryCostObjectIds(
    '',
    undefined,
    onApolloError(notify.error)
  );

  // Represents Team fields in the form.
  const teamInForm = {
    name: currentTeam.name,
    teamType: currentTeam.team_type,
    shiftStartTime,
    duration,
    internalOrderNumber: currentTeam.internal_order_number,
    teamRoleAssignments: currentTeam.team_role_assignments.map(
      (teamRoleAssignment) => {
        return {
          id: teamRoleAssignment.id,
          teamRoleGroup: teamRoleAssignment.team_role.role_group,
          teamRoleId: teamRoleAssignment.team_role_id,
          users: teamRoleAssignment.user_role_assignments.map(
            (userRoleAssignment) => userRoleAssignment.user
          ),
          userIds: teamRoleAssignment.user_role_assignments.map(
            (userRoleAssignment) => userRoleAssignment.user.id
          ),
        };
      }
    ),
  };

  useEffect(() => {
    // We do the lower case operation first to avoid it is being ..
    // .. duplicated executed multiple times in some().
    const lowerCaseNewTeamName = newTeamName.toLowerCase();
    const teamAlreadyExists =
      (teamNameQueryResult?.team.some(
        (team) => team.name.toLowerCase() === lowerCaseNewTeamName
      ) ??
        false) &&
      newTeamName !== '' &&
      teamInForm.name !== newTeamName;

    setNewTeamAlreadyExists(teamAlreadyExists);
  }, [teamNameQueryResult, newTeamName, teamInForm.name]);

  useEffect(() => {
    const [dayTime, nightTime] = dayShiftArrayFromUnknownSettings(
      currentTeam.team_settings
    ).map((n) => dayjs(n).utc());

    setShiftStartTime(dayTime);
    const duration =
      nightTime?.diff(dayTime, 'hours') ?? dayShiftHoursConstraint.min;
    setDuration(duration);
    setEndTime(add(dayTime?.valueOf() ?? 0, { hours: duration }));
  }, [currentTeam.team_settings]);

  if (loggedInUser === undefined) {
    return <PageLoadingSpinner />;
  }

  // When creating a Team, all the Team's corresponding data are also created in one transaction.
  const onCreateTeam = async (team: TeamInForm) => {
    const teamRoleAssignments: readonly CreateTeamRoleAssignmentEvent[] =
      team.teamRoleAssignments?.map((assignedTeamRole) => ({
        team_role_id: assignedTeamRole.teamRoleId,
        event_user_role_assignment_created:
          assignedTeamRole.userIds === undefined
            ? undefined
            : {
                data: assignedTeamRole.userIds.map((userId) => ({
                  user_id: userId,
                  event: eventData('user_role_assignment_created'),
                })),
              },
        event: eventData('team_role_assignment_created'),
      })) ?? [];

    await createTeam({
      variables: {
        teamType: team.teamType ?? TeamType.LocalIMT,
        name: nonEmptyString(team.name),
        internalOrderNumber: team.internalOrderNumber,
        teamSettings: teamSettings(team.startTime, team.duration),
        teamRoleAssignments: {
          data: teamRoleAssignments,
        },
      },
      onCompleted: (created) => {
        notify.success(successMessage.teamCreated);

        const createdTeamId = created.insert_event_team_created_one?.team_id;

        if (createdTeamId !== undefined) {
          navigate(
            routesWithPathParameter(routes.teamRoster, 'teamId', createdTeamId)
          );
        }
      },
    });
  };

  // When updating a Team, all the Team's corresponding data are also updated in one transaction.
  const onUpdateTeam = async (updatedTeam: TeamInForm, teamId: string) => {
    const internalOrderNumber = nonEmptyString(updatedTeam.internalOrderNumber);

    const updatedInternalOrderNumber =
      internalOrderNumber === undefined ? null : internalOrderNumber;

    await updateTeam({
      variables: {
        teamId,
        name: updatedTeam.name,
        teamType: updatedTeam.teamType ?? currentTeam.team_type,
        internalOrderNumber: permission.teamSetIon
          ? updatedInternalOrderNumber
          : internalOrderNumber,
        teamSettings: teamSettings(updatedTeam.startTime, updatedTeam.duration),
      },

      onCompleted: (): void => {
        notify.success(successMessage.teamUpdated);

        navigate(
          routesWithPathParameter(routes.teamRoster, 'teamId', currentTeam.id)
        );
      },
      refetchQueries: [teamByIdQueryName],
    });
  };

  const availableTeamTypes = teamTypes(permission.teamManageNonLocalImt);

  // Update the form field so that the UI can reflect the selection time.
  const onShiftPeriodChange = (time: dayjs.Dayjs | null) => {
    const shiftPeriodTime = getShiftPeriodTime(time);
    form.setFieldValue('startTime', shiftPeriodTime);
    setShiftStartTime(shiftPeriodTime ?? undefined);
  };

  return (
    <>
      {/*Read only*/}
      <Optional show={!permission.teamManageRosterAndTeam && !isNewTeam}>
        <ReadonlyTeam
          name={teamInForm.name}
          type={teamInForm.teamType}
          dayShiftPeriod={dayShiftFromUnknownSettings(
            currentTeam.team_settings
          )}
          roleAssignments={teamInForm.teamRoleAssignments.map(
            (roleAssignment) => ({
              roleId: roleAssignment.teamRoleId,
              users: roleAssignment.users.map((user) => ({
                firstName: user.first_name ?? '',
                lastName: user.last_name ?? '',
              })),
            })
          )}
        />
      </Optional>

      {/*Editor*/}
      {!(!permission.teamManageRosterAndTeam && !isNewTeam) && (
        <Row>
          <Optional show={!isNewTeam || !permission.teamManageNonLocalImt}>
            <Col span={18}>
              <Space style={{ float: 'right', marginBottom: '10px' }}>
                Team Type: {currentTeam.team_type}
              </Space>
            </Col>
          </Optional>
          <Col span={24}>
            <Form
              form={form}
              name="teamForm"
              labelCol={{ span: '8' }}
              labelAlign={'right'}
              labelWrap={true}
              colon={false}
              wrapperCol={{ span: '10' }}
              onFinish={(team: TeamInForm) => {
                if (isNewTeam) {
                  void onCreateTeam(team);
                } else {
                  void onUpdateTeam(team, currentTeam.id);
                }
              }}
              autoComplete="off"
              onValuesChange={(_changedValue, nextTeam) => {
                setNewTeamName(nextTeam.name);

                const nextTeamType = nextTeam.teamType;

                if (
                  nextTeamType === TeamType.CostedIMT ||
                  nextTeamType === TeamType.LocalIMT ||
                  nextTeamType === TeamType.EmergencyOperationsCentre ||
                  nextTeamType === TeamType.Section44
                ) {
                  setSelectedTeamType(nextTeamType);
                }

                if (
                  nextTeam.teamType === TeamType.EmergencyOperationsCentre &&
                  isNewTeam
                ) {
                  form.setFieldsValue({
                    internalOrderNumber: undefined,
                  });
                }

                setEndTime(
                  new Date(
                    hoursToMilliseconds(nextTeam.startTime.hour()) +
                      minutesToMilliseconds(nextTeam.startTime.minute()) +
                      hoursToMilliseconds(nextTeam.duration)
                  )
                );
              }}
            >
              <Form.Item
                label="Incident Event Name"
                name={'name'}
                shouldUpdate
                rules={[
                  {
                    required: true,
                    message: errorMessage.incidentNameNotGiven,
                  },
                  {
                    validator: validatorMaxLength(
                      errorMessage.incidentNameTooLong,
                      teamNameMaxLength
                    ),
                  },
                  {
                    validator: validatorMinLength(
                      errorMessage.incidentNameTooShort,
                      groupComponentsMinLength
                    ),
                  },
                  {
                    validator:
                      validatorGroupComponentRestrictedSpecialCharacters(
                        errorMessage.incidentNameSpecialCharacters
                      ),
                  },
                  {
                    validator: validatorNoWhiteSpaceOnEnds(
                      errorMessage.incidentEventHasWhitespaceOnEnds
                    ),
                  },
                ]}
                initialValue={teamInForm.name}
              >
                <Input id="teamForm_name" placeholder="Name" />
              </Form.Item>

              <Optional show={newTeamAlreadyExists}>
                <Row>
                  <Col span={8}></Col>
                  <Col span={10}>
                    <p style={{ textAlign: 'start' }}>
                      {errorMessage.incidentNameDuplicated}
                    </p>
                  </Col>
                </Row>
              </Optional>

              <Optional show={permission.teamManageNonLocalImt}>
                <Form.Item
                  name="teamType"
                  label="Type"
                  initialValue={teamInForm.teamType}
                  rules={[
                    { required: true, message: errorMessage.teamTypeNotGiven },
                  ]}
                >
                  <Select
                    className="teamForm_teamType"
                    id="teamForm_teamType"
                    options={availableTeamTypes}
                    title={'Team Type'}
                    style={{ height: '32px' }}
                  />
                </Form.Item>
              </Optional>

              {permission.teamManageNonLocalImt &&
                (selectedTeamType === TeamType.CostedIMT ||
                  selectedTeamType === TeamType.Section44) && (
                  <>
                    <Form.Item
                      //Note: At this stage, "Internal Order Number" is being used to refer
                      //to the backend "Cost Object ID" in the frontend UI ONLY.
                      //As such, only label, message, and placeholder refer to the order number like this.
                      label="Internal Order Number (ION)"
                      name="internalOrderNumber"
                      shouldUpdate
                      initialValue={teamInForm.internalOrderNumber}
                      rules={[
                        { required: true, message: errorMessage.ionNotGiven },
                      ]}
                    >
                      <Select
                        className="teamForm_internalOrderNumber"
                        id="teamForm_internalOrderNumber"
                        title={'Cost Object ID'}
                        style={{ height: '32px' }}
                        showEmptyOption
                        options={
                          activeAndDeletedCostObjects?.cost_object
                            .filter(
                              (o) =>
                                // Show all "Active" IONs
                                o.record_status === 'Active' ||
                                // If the ION is "Deleted", only show it if it is the already-selected ION.
                                // (i.e. for when editing a team that has a deleted ION.)
                                activeAndDeletedCostObjects.cost_object.find(
                                  (o) =>
                                    o.internal_order_number ===
                                    teamInForm.internalOrderNumber
                                )?.record_status === 'Deleted'
                            )
                            .map(
                              ({ internal_order_number }) =>
                                internal_order_number
                            ) ?? []
                        }
                        // Display the options in the format of "ION (Description)".
                        // The option value is the ION.
                        labeller={(value: string): string => {
                          const maybeDescription = descriptionOfCostObjectId(
                            activeAndDeletedCostObjects?.cost_object,
                            value
                          );
                          const description =
                            maybeDescription === undefined
                              ? ''
                              : ` (${maybeDescription})`;
                          return `${value}${description}`;
                        }}
                      />
                    </Form.Item>
                    <Row>
                      <Col sm={8}></Col>
                      <Col sm={10}>
                        <p
                          className="available-ions black-text-override"
                          hidden={!isNewTeam}
                        >
                          {"Available ION's are refreshed every 15 minutes."}
                        </p>
                      </Col>
                    </Row>
                  </>
                )}

              <Form.Item
                // Some states in AU, like NSW, VIC and ACT have daylight savings on and off and without automatic UTC conversion
                // this can produce inaccurate representation of shifts.
                // Day.js is native to AntD and is utilised here with UTC configurations.
                name="startTime"
                label="Shift Start Time"
                initialValue={
                  teamInForm.shiftStartTime ?? defaultDayShiftPeriod.start
                }
                required
              >
                <TimePicker
                  id={'day-shift-start-time-picker'}
                  format={shiftTimeFormat}
                  style={{ width: '100%' }}
                  aria-label="day-shift-time"
                  showNow={false}
                  allowClear={false}
                  onChange={onShiftPeriodChange}
                />
              </Form.Item>

              <Form.Item
                name="duration"
                label="Duration"
                initialValue={teamInForm.duration}
                required
              >
                <InputDuration
                  from={dayShiftHoursConstraint.min}
                  to={dayShiftHoursConstraint.max}
                  value={duration}
                  onChange={setDuration}
                />
              </Form.Item>

              {endTime === undefined ? undefined : (
                <Form.Item label="End Time">
                  <div>{format(utcToZonedTime(endTime, 'UTC'), 'HHmm')}</div>
                </Form.Item>
              )}

              {/* Anyone can create a Team, but only certain roles can manage/edit the team */}
              <Optional show={permission.teamManageRosterAndTeam || isNewTeam}>
                <Row style={{ marginTop: '30px' }} justify="end">
                  <Col span={8}>
                    <Button
                      id="team-save-btn"
                      type="primary"
                      htmlType="submit"
                      style={{ minWidth: '120px' }}
                      loading={updatingTeam || creatingTeam}
                    >
                      {isNewTeam ? 'Create Team' : 'Save'}
                    </Button>
                  </Col>
                </Row>
              </Optional>
            </Form>
          </Col>
        </Row>
      )}
    </>
  );
};

export default TeamForm;
